import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, Search, Filter } from 'lucide-react';
import { documentsApi } from '@/api/endpoints/documents.api';
import type { DocumentType } from '@/types/document.types';
import DocumentCard from './DocumentCard';
import DocumentUploadDialog from './DocumentUploadDialog';
import DocumentDetailDialog from './DocumentDetailDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface DocumentListProps {
  patientId: string;
  taskId?: string;
  demandId?: string;
  showUpload?: boolean;
  compact?: boolean;
}

const DocumentTypeOptions: Array<{ value: DocumentType | ''; label: string }> = [
  { value: '', label: 'Todos os tipos' },
  { value: 'LAB_RESULT', label: 'Resultado de Exame' },
  { value: 'PRESCRIPTION', label: 'Prescrição' },
  { value: 'MEDICAL_REPORT', label: 'Laudo Médico' },
  { value: 'IMAGING_REPORT', label: 'Exame de Imagem' },
  { value: 'DISCHARGE_SUMMARY', label: 'Sumário de Alta' },
  { value: 'REFERRAL', label: 'Encaminhamento' },
  { value: 'CERTIFICATE', label: 'Atestado' },
  { value: 'OTHER', label: 'Outro' },
];

export default function DocumentList({
  patientId,
  taskId,
  demandId,
  showUpload = true,
  compact = false,
}: DocumentListProps) {
  const queryClient = useQueryClient();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<DocumentType | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Query: Documents list
  const {
    data: documents = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['documents', 'patient', patientId, selectedType],
    queryFn: async () => {
      if (selectedType) {
        return documentsApi.getByPatientAndType(patientId, selectedType);
      }
      return documentsApi.getAllByPatient(patientId);
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Query: Document stats
  const { data: stats } = useQuery({
    queryKey: ['documents', 'stats', patientId],
    queryFn: () => documentsApi.getStatsByPatient(patientId),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', 'patient', patientId] });
    queryClient.invalidateQueries({ queryKey: ['documents', 'stats', patientId] });
  };

  const handleDelete = async (documentId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) {
      return;
    }
    try {
      await documentsApi.delete(documentId);
      handleRefresh();
    } catch (err) {
      console.error('Erro ao excluir documento:', err);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(search) ||
      doc.originalFileName.toLowerCase().includes(search) ||
      doc.documentTypeLabel.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Erro ao carregar documentos</p>
        <button
          onClick={handleRefresh}
          className="mt-2 text-sm text-red-600 hover:underline"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold">Documentos</h3>
          {stats && (
            <p className="text-sm text-muted-foreground">
              {stats.total} {stats.total === 1 ? 'documento' : 'documentos'}
            </p>
          )}
        </div>

        {showUpload && (
          <button
            onClick={() => setShowUploadDialog(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Enviar Documento
          </button>
        )}
      </div>

      {/* Filters */}
      {!compact && (
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border bg-background py-2 pl-10 pr-4 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as DocumentType | '')}
              className="rounded-lg border bg-background px-3 py-2 text-sm"
            >
              {DocumentTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Document Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
          <h4 className="mt-4 text-lg font-medium">Nenhum documento</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            {searchTerm || selectedType
              ? 'Nenhum documento encontrado com os filtros selecionados'
              : 'Nenhum documento foi enviado ainda'}
          </p>
          {showUpload && !searchTerm && !selectedType && (
            <button
              onClick={() => setShowUploadDialog(true)}
              className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Enviar primeiro documento
            </button>
          )}
        </div>
      ) : (
        <div className={compact ? 'space-y-2' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
          {filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onDelete={() => handleDelete(doc.id)}
              onViewDetails={() => setSelectedDocumentId(doc.id)}
              showActions={!compact}
            />
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      {showUploadDialog && (
        <DocumentUploadDialog
          patientId={patientId}
          taskId={taskId}
          demandId={demandId}
          onClose={() => setShowUploadDialog(false)}
          onSuccess={handleRefresh}
        />
      )}

      {/* Detail Dialog */}
      {selectedDocumentId && (
        <DocumentDetailDialog
          documentId={selectedDocumentId}
          onClose={() => setSelectedDocumentId(null)}
        />
      )}
    </div>
  );
}
